import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { ModalShell, ModalHeader } from '../../../components/ui/ModalFrame';
import { MODAL_LAYER } from '../../../constants/modalLayers';
import { PURCHASE_MODAL_BODY } from '../../../constants/layoutSpacing';
import { formatLicensePriceOrConsult } from '../../../utils/formatCurrencyBRL';
import { formatNeoCoins } from '../../../utils/formatNeoCoins';
import PaymentMethodModal from './PaymentMethodModal';
import SubscriptionPaymentModal from './SubscriptionPaymentModal';

const PurchaseLicenseModal = ({ license, purchaseMode = 'one_time', changePlan = false, onClose, onConfirm }) => {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const isSubscription = purchaseMode === 'subscription';
  const neoCoinsLabel = formatNeoCoins(license?.neo_coins ?? license?.neoCoins);

  return (
    <ModalShell
      backdropPadding="all"
      zClass={MODAL_LAYER.purchase}
      backdropClassName="bg-background/80 backdrop-blur-sm"
      panelClassName="relative w-full max-w-lg bg-card border border-border rounded-lg shadow-elevation-xl flex flex-col"
    >
      <ModalHeader
        title="Finalizar Compra"
        onClose={onClose}
        className="p-4 md:p-6 border-b border-border"
        titleClassName="text-xl md:text-2xl font-heading font-semibold text-foreground"
        closeButtonClassName="flex items-center justify-center w-8 h-8 rounded-lg text-muted-foreground hover:bg-muted transition-smooth"
      />

      <div className={PURCHASE_MODAL_BODY}>
        <div className="bg-muted/30 rounded-lg p-4 border border-border">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-lg font-heading font-semibold text-foreground">
                {license?.license_name}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Licença individual ou empresarial
              </p>
            </div>

            <div className="text-right">
              <p className="text-xl font-heading font-bold text-foreground">
                {formatLicensePriceOrConsult(license?.price)}
              </p>
            </div>
          </div>

          <div className="mt-4 space-y-2 text-sm">
            {neoCoinsLabel && (
              <div className="flex items-center gap-2 rounded-lg bg-amber-500/10 border border-amber-500/20 px-3 py-2">
                <Icon name="Coins" size={16} className="text-amber-500" />
                <span>
                  <strong>{neoCoinsLabel}</strong> Neo Coins incluídas
                </span>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Icon name="FileText" size={16} className="text-primary" />
              <span>
                <strong>{license?.max_documents}</strong> gerações de documentos
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Icon name="PenTool" size={16} className="text-primary" />
              <span>
                <strong>{license?.max_signatures}</strong> assinaturas de documentos
              </span>
            </div>

            {license?.is_shared && (
              <div className="flex items-center gap-2">
                <Icon name="Users" size={16} className="text-primary" />
                <span>
                  Permitido compartilhamento com até <strong>{license?.max_members}</strong> usuários
                </span>
              </div>
            )}

            {license?.features?.map((feature, index) => (
              <div key={index} className="flex items-center gap-2">
                <Icon name="Check" size={16} className="text-success" />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex gap-3">
          <Icon name={isSubscription ? 'Calendar' : 'CreditCard'} size={20} className="text-primary flex-shrink-0" />
          <p className="text-sm text-foreground">
            {isSubscription
              ? 'Assinatura mensal com cobrança automática no cartão de crédito via Mercado Pago. Você pode cancelar quando quiser e manter o acesso até o fim do período pago.'
              : 'As opções de pagamento (Cartão e PIX via Mercado Pago) serão exibidas na próxima etapa.'}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Button
            variant="outline"
            fullWidth
            onClick={onClose}
          >
            Cancelar
          </Button>

          <Button onClick={() => setShowPaymentModal(true)}>
            Continuar
          </Button>

          {showPaymentModal && isSubscription ? (
            <SubscriptionPaymentModal
              license={license}
              changePlan={changePlan}
              onClose={() => setShowPaymentModal(false)}
            />
          ) : showPaymentModal ? (
            <PaymentMethodModal
              license={license}
              onClose={() => setShowPaymentModal(false)}
            />
          ) : null}
        </div>
      </div>
    </ModalShell>
  );
};

export default PurchaseLicenseModal;
